import { ApolloClient, createHttpLink, InMemoryCache } from "@apollo/client";
import { createFragmentRegistry } from "@apollo/client/cache";
import { NetworkError } from "@apollo/client/errors";
import { ApolloLink } from "@apollo/client/link/core";
import { onError } from "@apollo/client/link/error";
import { Emitter } from "react-event-hook";

import { withApolloRetryLink } from "src/authentication/withApolloRetryLink.ts";
import { withAuthMiddleware } from "src/authentication/withAuthMiddleware.ts";
import { withXCSRFToken } from "src/authentication/withXCSRFToken.ts";
import { adminStatusFragment } from "src/graphql/anti-fraud/fragments/AdminStatusFragment.ts";
import { alertFragment } from "src/graphql/anti-fraud/fragments/AlertFragment.ts";
import { caseFragment } from "src/graphql/anti-fraud/fragments/CaseFragment.ts";
import { elementFragment } from "src/graphql/anti-fraud/fragments/ElementFragment.ts";
import { elementTypeFragment } from "src/graphql/anti-fraud/fragments/ElementTypeFragment.ts";
import { pageInfoFragment } from "src/graphql/anti-fraud/fragments/PageInfoFragment.ts";
import { propertyFragment } from "src/graphql/anti-fraud/fragments/PropertyFragment.ts";
import { ruleExecutionDetailsFragment } from "src/graphql/anti-fraud/fragments/RuleExecutionDetailsFragment.ts";
import { scenarioExecutionResultFragment } from "src/graphql/anti-fraud/fragments/ScenarioExecutionResultFragment.ts";
import { scenarioExecutionResultWithCaseFragment } from "src/graphql/anti-fraud/fragments/ScenarioExecutionResultWithCaseFragment.ts";
import { userFragment } from "src/graphql/anti-fraud/fragments/UserFragment.ts";
import { Message } from "src/hooks/useEvents.ts";

import fragmentTypes from "./fragment-types.json";

const onErrorInterceptor = (emitError: Emitter<Message>) => {
  return onError(({ networkError }) => {
    const error = networkError as NetworkError & { statusCode: number };
    if (/5[0-9][0-9]/.test(error?.statusCode?.toString() ?? ""))
      emitError({ code: error?.statusCode?.toString() ?? "", message: error?.message });
  });
};

export const createApolloClient = (emitError) => {
  return new ApolloClient({
    link: ApolloLink.from([
      withApolloRetryLink(),
      withAuthMiddleware(),
      withXCSRFToken(),
      onErrorInterceptor(emitError),
      createHttpLink({ uri: "/v1/graphql" }),
    ]),
    cache: new InMemoryCache({
      possibleTypes: fragmentTypes,
      fragments: createFragmentRegistry(
        caseFragment,
        userFragment,
        ruleExecutionDetailsFragment,
        scenarioExecutionResultFragment,
        scenarioExecutionResultWithCaseFragment,
        pageInfoFragment,
        alertFragment,
        adminStatusFragment,
        propertyFragment,
        elementTypeFragment,
        elementFragment
      ),
    }),
  });
};
